import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import { Button } from "../../components/ui/button";
import { ArrowLeft, AlertTriangle, Loader2, RefreshCw, PoundSterling } from "lucide-react";
import { toast } from "sonner";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { getUserRole, ROLES } from "../../config/roleConfig";
import { STAFF } from "../../config/staffConfig";

// time-recording-foundations: the fee earners a grade can be assigned to.
// Mirrors the backend gate (hub.staff_config.resolve_staff) -- active fee
// earners only, same roster the rest of the app already treats as
// authoritative.
const FEE_EARNERS = STAFF.filter((s) => s.is_active && s.is_fee_earner);

function fmtGbp(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

function SetGradeModal({ feeEarner, currentAssignment, grades, onClose, onDone }) {
  const [grade, setGrade] = useState(currentAssignment?.grade || grades[0]?.grade || "");
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = grade && effectiveFrom && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await axios.post(`${API}/fee-earner-rates`, {
        fee_earner_email: feeEarner.email,
        grade,
        effective_from: effectiveFrom,
        notes: notes.trim(),
      });
      toast.success(`Grade set for ${feeEarner.full_name}`);
      onDone();
    } catch (err) {
      toast.error(errorMessage(err, "Could not set grade"));
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="set-grade-modal"
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg text-slate-100 mb-1">Set grade — {feeEarner.full_name}</h3>
        {currentAssignment && (
          <p className="text-sm text-slate-400 mb-4">
            Current: {currentAssignment.grade} since {currentAssignment.effective_from}.
            Setting a new grade closes that period on the date below.
          </p>
        )}
        <label className="flex flex-col gap-1 text-sm text-slate-300 mb-3">
          <span>Grade</span>
          <select
            data-testid="set-grade-select"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-slate-100"
          >
            {grades.map((g) => (
              <option key={g.grade} value={g.grade}>
                {g.grade} — £{g.rate}/hr (£{g.expedite}/hr complex/expedite)
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm text-slate-300 mb-3">
          <span>Effective from</span>
          <input
            data-testid="set-grade-effective-from-input"
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-slate-100"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-slate-300 mb-4">
          <span>Notes (optional)</span>
          <textarea
            data-testid="set-grade-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-slate-100"
          />
        </label>
        <div className="flex justify-end gap-2">
          <Button
            data-testid="set-grade-cancel"
            variant="ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            data-testid="set-grade-confirm"
            onClick={submit}
            disabled={!canSubmit}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function FeeEarnerRates() {
  const navigate = useNavigate();
  const { email, isAdmin, loading: userLoading } = useCurrentUser();
  const role = getUserRole(email);
  const canManageRates = role === ROLES.DIRECTOR || isAdmin;

  const [assignmentsByEmail, setAssignmentsByEmail] = useState({});
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFeeEarner, setActiveFeeEarner] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ratesRes, gradesRes] = await Promise.all([
        axios.get(`${API}/fee-earner-rates`),
        axios.get(`${API}/fee-earner-rates/grades`),
      ]);
      const byEmail = {};
      for (const r of ratesRes.data?.rates || []) {
        if (r.effective_to === null) byEmail[r.fee_earner_email] = r;
      }
      setAssignmentsByEmail(byEmail);
      setGrades(gradesRes.data?.grades || []);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load rates"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManageRates) refresh();
  }, [canManageRates, refresh]);

  if (userLoading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!canManageRates) {
    return (
      <div
        data-testid="fee-earner-rates-forbidden"
        className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6"
      >
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-status-amber mb-3" />
          <h2 className="text-lg mb-2">Director / admin only</h2>
          <p className="text-sm text-slate-400 mb-4">
            Fee-earner billing rates are restricted to director and admin accounts.
          </p>
          <Button data-testid="fee-earner-rates-forbidden-back" onClick={() => navigate("/")}>
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-6">
          <Button data-testid="fee-earner-rates-back" variant="ghost" size="sm" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <PoundSterling className="w-5 h-5 text-teal" />
          <h1 className="text-2xl">Fee earner rates</h1>
          <div className="flex-1" />
          <Button data-testid="fee-earner-rates-refresh" variant="outline" size="sm" onClick={refresh} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>

        <p className="text-sm text-slate-400 mb-4">
          Each fee earner's billing grade. The £ figures come from the firm's
          one rates table and are never typed here — setting a new grade
          closes the previous period on its effective-from date, so past
          time entries keep the grade that applied when the work was done.
        </p>

        <table data-testid="fee-earner-rates-table" className="w-full text-sm">
          <thead className="text-left text-slate-400 border-b border-slate-800">
            <tr>
              <th className="py-2 px-3">Fee earner</th>
              <th className="py-2 px-3">Grade</th>
              <th className="py-2 px-3">Rate</th>
              <th className="py-2 px-3">Effective from</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {FEE_EARNERS.map((fe) => {
              const assignment = assignmentsByEmail[fe.email];
              return (
                <tr
                  key={fe.email}
                  data-testid={`fee-earner-rates-row-${fe.initials}`}
                  className="border-b border-slate-800/50 hover:bg-slate-900/50"
                >
                  <td className="py-2 px-3 text-slate-200">{fe.full_name}</td>
                  <td className="py-2 px-3">
                    {assignment ? assignment.grade : <span className="text-status-amber">Not set</span>}
                  </td>
                  <td className="py-2 px-3 text-slate-400">
                    {assignment
                      ? `${fmtGbp(assignment.standard_rate)}/hr (${fmtGbp(assignment.complex_expedite_rate)}/hr complex/expedite)`
                      : "—"}
                  </td>
                  <td className="py-2 px-3 text-slate-400">{assignment?.effective_from || "—"}</td>
                  <td className="py-2 px-3 text-right">
                    <Button
                      data-testid={`fee-earner-rates-set-${fe.initials}`}
                      size="sm"
                      variant="outline"
                      onClick={() => setActiveFeeEarner(fe)}
                    >
                      {assignment ? "Change grade" : "Set grade"}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {activeFeeEarner && (
        <SetGradeModal
          feeEarner={activeFeeEarner}
          currentAssignment={assignmentsByEmail[activeFeeEarner.email]}
          grades={grades}
          onClose={() => setActiveFeeEarner(null)}
          onDone={() => { setActiveFeeEarner(null); refresh(); }}
        />
      )}
    </div>
  );
}
